/**
 * Simple Wholesale Lead Service
 * Process properties from any source, enrich with Melissa + BatchData, save to Firebase
 */

import { melissaService } from './melissa.js';
import { lookupPhoneNumbers } from './batchdata.js';
import { submitLeadToFirebase } from './firebase.js';
import { sendLeadNotifications } from './notifications.js';
import { v4 as uuidv4 } from 'uuid';

class WholesaleService {
  constructor() {
    this.processedProperties = new Set();
  }

  /**
   * Process a wholesale property from any source
   * @param {Object} property - Property data
   * @param {string} property.address - Full address
   * @param {string} property.source - Source name (zillow, craigslist, etc)
   * @param {number} property.price - Asking price (optional)
   * @param {string} property.url - Source URL (optional)
   * @param {string} property.description - Property description (optional)
   * @returns {Promise<Object|null>} - Processed lead or null
   */
  async processProperty(property) {
    const { address, source, price, url = '', description = '' } = property;
    
    // Prevent duplicates
    const key = `${address}_${source}`;
    if (this.processedProperties.has(key)) {
      console.log(`⚠️ Already processed: ${address}`);
      return null;
    }

    console.log(`🏠 Processing wholesale property: ${address}`);
    
    try {
      // Step 1: Get property data from Melissa
      const melissaData = await melissaService.lookupPropertyInfo(address);
      
      // Step 2: Get contact info from BatchData
      const batchData = await this.getBatchDataContact(address);
      
      // Step 3: Score the lead
      const score = this.calculateScore({ melissaData, price, description });
      
      // Step 4: Create wholesale lead
      const lead = this.createWholesaleLead({
        address, source, price, url, description, 
        melissaData, batchData, score
      });
      
      // Step 5: Save to Firebase
      const leadId = await submitLeadToFirebase(lead);
      if (!leadId) return null;
      
      lead.id = leadId;
      
      // Step 6: Notify if high score
      if (score >= 7) {
        await this.sendNotification(lead);
      }
      
      this.processedProperties.add(key);
      console.log(`✅ Wholesale lead saved: ${leadId} (Score: ${score})`);
      
      return lead;
      
    } catch (error) {
      console.error(`❌ Error processing ${address}:`, error);
      return null;
    }
  }

  /**
   * Get contact info from BatchData
   */
  async getBatchDataContact(address) {
    try {
      // Parse address for BatchData format
      const parts = address.split(',').map(s => s.trim());
      if (parts.length < 3) return null;
      
      const addressObj = {
        street: parts[0],
        city: parts[1],
        state: parts[2].split(' ')[0],
        zip: parts[2].split(' ')[1] || ''
      };
      
      return await lookupPhoneNumbers(addressObj);
    } catch (error) {
      console.warn('BatchData lookup failed:', error);
      return null;
    }
  }

  /**
   * Calculate wholesale score (1-10)
   */
  calculateScore({ melissaData, price, description }) {
    let score = 5;
    
    // Distress keywords
    const distressWords = ['as-is', 'cash only', 'needs work', 'fixer', 'handyman', 'motivated', 'must sell'];
    const descLower = description.toLowerCase();
    score += distressWords.filter(word => descLower.includes(word)).length;
    
    // Equity bonus
    if (melissaData?.apiPercentage > 50) score += 1;
    if (melissaData?.apiPercentage > 80) score += 1;
    
    // Price vs value
    if (price && melissaData?.apiEstimatedValue && price < melissaData.apiEstimatedValue * 0.8) {
      score += 2; // 20%+ below market
    }
    
    return Math.min(10, Math.max(1, Math.round(score)));
  }

  /**
   * Create wholesale lead object
   */
  createWholesaleLead({ address, source, price, url, description, melissaData, batchData, score }) {
    const phone = batchData?.phoneNumbers?.[0] || '';
    const email = batchData?.emails?.[0] || '';
    
    return {
      // Lead basics
      type: 'wholesale',
      leadSource: `Wholesale - ${source}`,
      campaign_name: 'wholesale_sourcing',
      createdAt: new Date().toISOString(),
      
      // Property info
      address,
      city: melissaData?.city || '',
      state: melissaData?.state || '',
      zip: melissaData?.zip || '',
      price: price || 0,
      
      // Owner/Contact
      name: melissaData?.apiOwnerName || 'Property Owner',
      phone,
      email,
      
      // Property details
      estimatedValue: melissaData?.apiEstimatedValue || 0,
      formattedEstimatedValue: melissaData?.formattedApiEstimatedValue || '',
      equity: melissaData?.apiEquity || 0,
      formattedEquity: melissaData?.formattedApiEquity || '',
      equityPercentage: melissaData?.apiPercentage || 0,
      bedrooms: melissaData?.bedrooms || '',
      bathrooms: melissaData?.bathrooms || '',
      squareFootage: melissaData?.finishedSquareFootage || 0,
      
      // Wholesale specific
      wholesaleScore: score,
      source,
      url,
      description,
      allPhones: batchData?.phoneNumbers || [],
      allEmails: batchData?.emails || []
    };
  }

  /**
   * Send notification for high-value leads
   */
  async sendNotification(lead) {
    try {
      await sendLeadNotifications(lead, {
        source: `Wholesale - ${lead.source}`,
        pushoverTitle: `🏠 Wholesale Lead (${lead.wholesaleScore}/10)`,
        pushoverPriority: 1
      });
    } catch (error) {
      console.error('Notification failed:', error);
    }
  }

  /**
   * Export leads to CSV for Close CRM
   */
  exportCSV(leads) {
    const headers = [
      'Address', 'City', 'State', 'Zip', 'Owner_Name', 'Phone', 'Email',
      'Estimated_Value', 'Equity', 'Score', 'Source', 'Date_Found'
    ];

    const rows = leads.map(lead => [
      lead.address || '',
      lead.city || '',
      lead.state || '',
      lead.zip || '',
      lead.name || '',
      lead.phone || '',
      lead.email || '',
      lead.estimatedValue || 0,
      lead.equity || 0,
      lead.wholesaleScore || 0,
      lead.source || '',
      lead.createdAt || ''
    ]);

    return [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
  }

  /**
   * Manual property entry for testing
   */
  async addManualProperty(address, source = 'manual') {
    return this.processProperty({ address, source });
  }
}

// Export singleton
export const wholesaleService = new WholesaleService();
export default wholesaleService;